import React from 'react';
import { Fieldset, Spacing, useNotifications } from '@doveit/bricks';
import { Contact } from '../../../providers/api/dtos/contact';
import { createLead } from '../../../providers/api/lead/leadProvider';
import { CreateLeadFormExtendedModel } from '../../models/CreateLeadFormExtendedModel';
import CreateLeadForm from '../../components/create-lead-form/CreateLeadForm';
import { Lead } from '../../../providers/api/dtos/lead';
import { LeadSource, LeadStatus, LeadWillingness } from '../../../domain/types';
import useGoogleMapsAPI from '../../../hooks/use-google-maps-api/useGoogleMapsAPI';
import useContact from '../../../contact/hooks/use-contact/useContact';
import createOrUpdateContact from '../../../contact/utils/createOrUpdateContact';
import { saveLeadContactCallBackDate } from '../../../providers/api/call-center/callCenterLeadProvider';
import { formDateTimeToDate } from '../../../utils/form';
import { geolocate } from '../../../providers/public-api/location/locationProvider';
import CardSkeleton from '../../../components/card/skeleton/CardSkeleton';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { ProspectFormByLeadProps } from '../../../prospect/components/prospect-form-by-lead/ProspectFormByLead';
import SelectContact from '../../../contact/containers/select-contact/SelectContact';
import { ContactStatus } from '../../../contact/containers/select-contact/types';
import { booleanStringToBool } from '../../../utils/boolean-string/booleanString';

export interface CreateLeadProps {
  lead?: Partial<Lead>,
  contactId?: string,
  initialValues?: Partial<CreateLeadFormExtendedModel>,
  hideFormSubmit?: boolean,
  formRef?: ProspectFormByLeadProps['innerRef'],
  onSuccess?: (contact: Contact, lead: Lead) => void,
  onFormStatusChanged?: (status: boolean) => void,
}

export const LOAD_CONTACT_ERROR_MESSAGE = 'Errore durante il caricamento del contatto. Riprova inserendo i dati manualmente.';

const CreateLead: React.FC<CreateLeadProps> = ({
  lead,
  contactId,
  initialValues,
  formRef,
  hideFormSubmit,
  onSuccess,
  onFormStatusChanged,
}) => {
  const { userIsAdmin, userIsCallCenter } = useRBAC();

  const { getPlaceDetails } = useGoogleMapsAPI();
  const [isSaving, setIsSaving] = React.useState(false);
  const [selectedContact, setSelectedContact] = React.useState<Contact | undefined>();
  const [overwriteValues, setOverwriteValues] = React.useState<Partial<Contact> | undefined>();
  const [formActionDisabled, setFormActionDisabled] = React.useState(false);

  const { data: initialContact, error: initialContactError } = useContact(contactId);
  const { addSuccess, addError, addWarning } = useNotifications();

  React.useEffect(() => {
    if (initialContact) {
      setSelectedContact(initialContact);
    }

    if (initialContactError) {
      addWarning(LOAD_CONTACT_ERROR_MESSAGE);
    }
  }, [initialContact, initialContactError, addWarning]);

  const onSubmit = React.useCallback(async (values: CreateLeadFormExtendedModel) => {
    if (!overwriteValues && !selectedContact) {
      addError('Per creare la lead, devi aggiungere un contatto');
      return;
    }

    if (!(overwriteValues?.phoneNumber || selectedContact?.phoneNumber)) {
      addError('Non è possibile creare una lead per un contatto senza numero di telefono. Scegli o crea un altro contatto');
      return;
    }

    setIsSaving(true);

    try {
      const place = await getPlaceDetails(values.propertyAddress?.placeId!);
      const contact = await createOrUpdateContact(overwriteValues, selectedContact);

      const { suburb } = (place.latitude && place.longitude)
        ? await geolocate({
          latitude: `${place.latitude}`,
          longitude: `${place.longitude}`,
        }).catch((_) => ({ suburb: undefined }))
        : { suburb: undefined };

      const createdLead = await createLead({
        ...lead,
        contactId: contact.id!,
        status: LeadStatus.IN_PROGRESS,
        propertyAddress: values.propertyAddress?.description!,
        propertyFloor: values.propertyFloor ? parseFloat(values.propertyFloor) : undefined,
        propertySize: parseInt(values.propertySize, 10),
        propertyStatus: values.propertyStatus,
        propertyType: values.propertyType,
        propertyFeatures: values.propertyFeatures.reduce((acc, curr) => ({
          ...acc,
          [curr]: true,
        }), {}),
        locality: place.locality,
        area: suburb?.area,
        plateCode: place.plateCode,
        postalCode: place.postalCode,
        latitude: place.latitude,
        longitude: place.longitude,
        source: values.source,
        residualMortgage: false,
        otherAgencies: false,
        inheritedProperty: false,
        giftedProperty: false,
        multiOwner: false,
        ownedByCompany: false,
        registryDiscrepancies: false,
        subsidizedHousing: false,
        willingness: values.willingness ?? LeadWillingness.NOW,
        utmCampaign: values?.source === LeadSource.B2B ? 'b2b-development' : undefined,
        originAgentId: values?.originAgent?.id,
        isCold: booleanStringToBool(values.isCold) ?? false,
        createdAt: (values.createdAtDate && values.createdAtTime)
          ? formDateTimeToDate(values.createdAtDate, values.createdAtTime).toISOString()
          : undefined,
      });

      if (values.nextOpportunityDate) {
        const nextOpportunityTime = values.nextOpportunityTime || '10:00';
        await saveLeadContactCallBackDate(contact.id!, formDateTimeToDate(values.nextOpportunityDate, nextOpportunityTime));
      }

      setIsSaving(false);
      addSuccess('Lead salvata con successo');

      if (onSuccess) {
        onSuccess(contact, createdLead);
      }
    } catch (err) {
      setIsSaving(false);
      addError('Non è stato possibile salvare la lead');
    }
  }, [addError, addSuccess, getPlaceDetails, lead, onSuccess, overwriteValues, selectedContact]);

  React.useEffect(() => onFormStatusChanged?.(formActionDisabled), [formActionDisabled, onFormStatusChanged]);

  const onContactChange = React.useCallback((status: ContactStatus, contactData?: Contact, search?: Partial<Contact>) => {
    setSelectedContact(contactData);
    setOverwriteValues(search);
    setFormActionDisabled(status === 'DUPLICATE');
  }, []);

  return (
    <>
      <Fieldset legend="Dati contatto">
        {contactId && !initialContact && !initialContactError ? (
          <CardSkeleton data-ref="contact-skeleton" />
        ) : (
          <SelectContact
            onContactChange={onContactChange}
            initialContact={initialContact}
            showClearContact={!contactId}
          />
        )}
      </Fieldset>
      <Spacing margin={[300, 0, 0]}>
        <CreateLeadForm
          loading={isSaving}
          initialValues={initialValues}
          canSetAsPersonal={userIsAdmin || userIsCallCenter}
          innerRef={formRef}
          hideSubmit={hideFormSubmit}
          onSubmit={onSubmit}
          disabled={formActionDisabled}
        />
      </Spacing>
    </>
  );
};

export default CreateLead;
